'use client';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Container, Paper } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { act, useEffect, useState } from 'react';
import ThemeProvider from '../../theme/ThemeProvider';
import TokenCard from './TokenCard';
import { useAnimation, motion, useCycle } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import React from 'react';
import { useDAppState } from '../../context/DAppStateProvider';
import { NATIVE_TOKEN, DEFAULT_DAPP_STATE } from '../../utils';

import { toast } from 'react-toastify';

const TokensPage = () => {
  const navigate = useNavigate();
  const tokens = NATIVE_TOKEN;
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  // Array of texts to cycle through
  const subtitleTexts = [
    'Explore NFTs across multiple blockchain networks',
    'NFTs are the next frontier for digital ownership',
    'We’re witnessing the birth of a new economy',
    'The world remembers extraordinary, not ordinary',
  ];
  const [currentSubtitleIndex, setCurrentSubtitleIndex] = useState(0);

  const { walletConnection, handleDisconnect, switchNetwork, switchableNetwork } = useDAppState();
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const handleTokenClick = (network) => {
    if (network === walletConnection.network) {
      navigate(`/collection/${network}`);
    } else if (switchableNetwork.includes(network)) {
      switchNetwork(network);
      navigate(`/collection/${network}`);
    } else {
      handleDisconnect();
      setSelectedNetwork(network);
    }
  };

  useEffect(() => {
    if (
      selectedNetwork &&
      walletConnection.network != selectedNetwork &&
      walletConnection.connectionState === 'disconnected'
    ) {
      navigate(`/collection/${selectedNetwork}`);
    }
  }, [selectedNetwork, walletConnection]);

  const controls = useAnimation();
  const [ref, inView] = useInView({ triggerOnce: false });

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }
    if (inView) {
      controls
        .start({
          x: 0, // Final position (center)
          transition: {
            duration: 3,
            ease: [1.1, 0.01, -0.05, 1.9], // Smooth easing
            delay: 0.3,
          },
        })
        .then(() => {
          // After pausing, flow left again
          controls.start({
            x: -100,
            opacity: 0,
            transition: { delay: 1, duration: 1 },
          });
        });
    } else {
      controls.start({ x: '100vw', opacity: 1 }); // Reset to right
    }
  }, [inView, controls, prefersReducedMotion]);

  const [animation, cycleAnimation] = useCycle(
    { x: '-50%', opacity: 0 }, // Start state
    { x: '50%', opacity: 0 }, // Start state
    { x: 0, opacity: 1 }, // Slide in
    { x: 0, opacity: 1 }, // Pause
    { x: '-50%', opacity: 0 }, // Slide out
  );

  // Effect to cycle through subtitle texts
  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }
    const interval = setInterval(() => {
      setCurrentSubtitleIndex((prevIndex) => (prevIndex + 1) % subtitleTexts.length);
    }, 6000); // Change text every 2 seconds

    return () => clearInterval(interval);
  }, [prefersReducedMotion, subtitleTexts.length]);

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }
    const interval = setInterval(() => cycleAnimation(), 3000); // Repeat every 3s
    return () => clearInterval(interval);
  }, [cycleAnimation, prefersReducedMotion]);

  return (
    <ThemeProvider>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper
          elevation={3}
          sx={{
            overflowX: 'hidden',
            p: 3,
            mb: 8,
            mt: 5,
            borderRadius: 2,
            background: 'rgba(19, 19, 26, 0.7)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 0 20px rgba(0, 229, 255, 0.15)',
          }}
        >
          <Typography
            id="animated-title"
            variant="h4"
            component={prefersReducedMotion ? 'h1' : motion.h1}
            ref={ref}
            initial={prefersReducedMotion ? false : { x: '100vw', opacity: 1 }}
            animate={prefersReducedMotion ? undefined : animation}
            transition={prefersReducedMotion ? undefined : { duration: 1.5, ease: 'easeInOut' }}
            gutterBottom
            fontWeight="bold"
            sx={{
              textAlign: 'center',
              color: '#fff',
              animation: prefersReducedMotion
                ? 'none'
                : 'glow-nft-marketplace 2s infinite alternate',
              '@keyframes glow-nft-marketplace': {
                '0%': { textShadow: '0 0 5px rgba(0, 229, 255, 0.5)' },
                '100%': { textShadow: '0 0 15px rgba(0, 229, 255, 1)' },
              },
            }}
          >
            NFT Marketplace
          </Typography>
          <Typography
            id="animated-subtitle"
            variant="body1"
            color="text.secondary"
            paragraph
            sx={{
              textAlign: 'center',
              animation: prefersReducedMotion
                ? 'none'
                : 'fadeIn-description 24s infinite ease-out, float-description 6s infinite ease-in-out, glow-description 1s infinite alternate',
              '@keyframes fadeIn-description': {
                '0%, 25%, 50%, 75%, 100%': { opacity: 0 },
                '12.5%, 37.5%, 62.5%, 87.5%': { opacity: 1 },
              },
              '@keyframes float-description': {
                '0%, 100%': { transform: 'translateY(20px)' },
                '10%, 90%': { transform: 'translateY(0)' },
              },
              '@keyframes glow-description': {
                '0%': { textShadow: '0 0 5px rgba(0, 229, 255, 0.5)' },
                '100%': { textShadow: '0 0 15px rgba(0, 229, 255, 1)' },
              },
            }}
          >
            {subtitleTexts[prefersReducedMotion ? 0 : currentSubtitleIndex]}
          </Typography>
        </Paper>

        <Paper
          elevation={3}
          sx={{
            p: 5,
            mb: 8,
            mt: 5,
            borderRadius: 2,
            background: 'rgba(19, 19, 26, 0.7)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 0 20px rgba(0, 229, 255, 0.15)',
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{
              mb: 5,
              py: 1,
              textAlign: 'center',
              color: '#fff',
              borderRadius: 2,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 0 20px rgba(0, 229, 255, 0.15)',
            }}
          >
            Select a Network
          </Typography>

          <Grid container spacing={3}>
            {tokens.map((token) => (
              <Grid key={token.name} size={{ xs: 12, sm: 6, md: 3 }}>
                <TokenCard token={token} onClick={() => handleTokenClick(token.name)} />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default TokensPage;
